import { useCallback, useEffect, useRef } from 'react';
import { useAnalytics } from '../../../services/tracking';
import { calculateNumItems } from '../../../services/tracking/events/utils/tracking.utils';
import type { FormData, PaymentInformationData } from '../../../types';
import type { ShopifyOfferDto } from '../../../offer.types';
import type { CheckoutResponseDto } from '../../../services/checkout/checkout.types';
import { getOfferTotal } from '../../../utils/pricing';

export const useCheckoutAnalytics = (
  shopifyOffer: ShopifyOfferDto,
  formData: FormData,
  storeId?: string
) => {
  const analytics = useAnalytics();
  const currency = 'BRL';

  // begin_checkout deve disparar uma única vez por checkout. O effect re-roda
  // quando o pricing chega (e a cada mudança de total), então travamos após o
  // primeiro disparo válido para não super-contar InitiateCheckout.
  const beginCheckoutFiredRef = useRef(false);

  // Setup analytics installation listener
  useEffect(() => {
    function onAnalyticsInstallationDone(_evt: any) {
      // analytics installed
    }
    window.addEventListener('analyticsInstallationDone', onAnalyticsInstallationDone);
    return () => window.removeEventListener('analyticsInstallationDone', onAnalyticsInstallationDone);
  }, []);

  // Track page view
  useEffect(() => {
    analytics.trackPageView();
  }, [storeId, analytics]);

  // Track begin checkout
  useEffect(() => {

    if (!analytics || beginCheckoutFiredRef.current) {
      return;
    }

    const offerItems = (shopifyOffer?.items ?? []).filter(i => i.source === 'offer');
    if (offerItems.length === 0) {
      return;
    }

    // O valor vem do pricing canônico, que pode chegar DEPOIS do nanoId.
    // Se dispararmos com value=0, o validateEventData descarta o evento em
    // silêncio e ele nunca re-dispara (o effect não re-roda). Aguardamos o
    // valor existir; quando o pricing chega, a dep `pricing.total` re-dispara.
    const value = getOfferTotal(shopifyOffer);
    if (value <= 0) {
      return;
    }

    beginCheckoutFiredRef.current = true;

    const items = offerItems.map(item => ({
      ...item,
      item_id: String(item.variant_id),
      item_name: item.name,
      price: Number(item.price),
      quantity: item.quantity,
      image_url: item.imageUrl ?? undefined,
    }));

    const contentIds = offerItems.map(item => String(item.variant_id));
    const numItems = calculateNumItems(items); // ✅ Usa função centralizada

    analytics.trackBeginCheckout({
      event: 'begin_checkout',
      ecommerce: {
        currency,
        value,
        coupon: undefined,
        items,
      },
      fb_data: {
        currency,
        value,
        content_ids: contentIds,
        content_type: 'product',
        num_items: numItems,
      },
    });

  }, [shopifyOffer.nanoId, shopifyOffer.pricing?.total, analytics, currency]);

  const trackAddShippingInfo = useCallback(() => {
    const items = formData.orderSummary.products.map(product => ({
      ...product,
      item_id: String(product.id),
      item_name: product.name,
      price: Number(product.price),
      quantity: product.quantity,
    }));
    const value = formData.orderSummary.total - (Number(formData.orderSummary.discountAmount) || 0);
    const shipping_tier = 'PAC';
    const contentIds = (shopifyOffer.items ?? [])
      .filter(i => i.source === 'offer')
      .map(item => String(item.variant_id));
    const numItems = calculateNumItems(items); // ✅ Usa função centralizada

    analytics.trackAddShippingInfo({
      event: 'add_shipping_info',
      ecommerce: {
        currency,
        value,
        items,
        coupon: formData.orderSummary.couponCode,
        shipping_tier,
      },
      fb_data: {
        currency,
        value,
        content_ids: contentIds,
        content_type: 'product',
        shipping_tier,
        num_items: numItems,
      },
    });
  }, [analytics, currency, formData.orderSummary, shopifyOffer.items]);

  const trackAddPaymentInfo = useCallback((paymentInformation: PaymentInformationData) => {
    if (!paymentInformation.paymentMethod || paymentInformation.paymentMethod.trim() === '') {
      return;
    }

    const items = formData.orderSummary.products.map(product => ({
      ...product,
      item_id: String(product.id),
      item_name: product.name,
      price: Number(product.price),
      quantity: product.quantity,
    }));
    const value = formData.orderSummary.total - (Number(formData.orderSummary.discountAmount) || 0);
    const contentIds = (shopifyOffer.items ?? [])
      .filter(i => i.source === 'offer')
      .map(item => String(item.variant_id));
    const numItems = calculateNumItems(items); // ✅ Usa função centralizada
    
    analytics.trackAddPaymentInfo({
      event: 'add_payment_info',
      ecommerce: {
        currency,
        value,
        items,
        coupon: formData.orderSummary.couponCode,
        payment_type: paymentInformation.paymentMethod,
      },
      fb_data: {
        currency,
        value,
        content_ids: contentIds,
        content_type: 'product',
        payment_type: paymentInformation.paymentMethod,
        num_items: numItems,
      },
    });
  }, [analytics, currency, formData.orderSummary, shopifyOffer.items]);

  const trackPurchase = useCallback((orderResponse: CheckoutResponseDto) => {
    if (!orderResponse?.paymentData?.details) {
      return;
    }

    const items = formData.orderSummary.products.map(product => ({
      ...product,
      item_id: String(product.id),
      item_name: product.name,
      price: Number(product.price),
      quantity: product.quantity,
    }));

    const orderNanoId = orderResponse.order.nanoId?.trim() || '';

    const value = Number(orderResponse.order.totalPrice) - (Number(orderResponse.order.totalDiscounts) || 0);
    const transaction_id = orderResponse.order.gatewayInfos?.gatewayId 
      ? String(orderResponse.order.gatewayInfos.gatewayId) 
      : orderNanoId || '';
    const contentIds = (shopifyOffer.items ?? [])
      .filter(i => i.source === 'offer')
      .map(item => String(item.variant_id));
    const numItems = calculateNumItems(items); // ✅ Usa função centralizada

    analytics.trackPurchase({
      event: 'purchase',
      orderId: orderNanoId,
      ecommerce: {
        currency,
        value,
        items,
        coupon: formData.orderSummary.couponCode,
        transaction_id,
      },
      fb_data: {
        currency,
        value,
        content_ids: contentIds,
        content_type: 'product',
        num_items: numItems,
        contents: items.map(item => ({
          id: String(item.item_id),
          quantity: item.quantity || 1,
          item_price: item.price,
        })),
      },
      ads_data: {
        value,
        currency,
        transaction_id,
      },
      client_id: String(storeId),
      user_email: formData.userIdentification?.email,
    });
  }, [analytics, currency, formData.orderSummary, shopifyOffer.items, storeId]);

  return {
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackPurchase,
  };
}; 